import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    collection,
    addDoc,
    getDocs,
    doc,
    updateDoc,
    deleteDoc,
    query,
    orderBy,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import {
    ChevronLeft,
    Plus,
    Edit3,
    Trash2,
    Save,
    X,
    AlertCircle,
    CheckCircle,
    Loader2,
    Building2,
    CreditCard,
    Wallet,
    DollarSign,
    Banknote,
    Shield,
    Eye,
    EyeOff,
} from "lucide-react";

const XFinancePaymentMethods = () => {
    const { user } = useUserAuth();
    const navigate = useNavigate();

    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMethods, setLoadingMethods] = useState(true);
    const [message, setMessage] = useState({ text: "", type: "" });
    const [editingMethod, setEditingMethod] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newMethod, setNewMethod] = useState({
        nombre: "",
        tipo: "credito",
        entidad: "",
        ultimos4: "",
        limite: "",
        fechaVencimiento: "",
        activo: true,
        color: "#3B82F6"
    });

    const methodTypes = [
        { value: "credito", label: "Tarjeta de Crédito", icon: CreditCard },
        { value: "debito", label: "Tarjeta de Débito", icon: CreditCard },
        { value: "efectivo", label: "Efectivo", icon: Banknote },
        { value: "transferencia", label: "Transferencia", icon: Shield },
    ];

    const colorOptions = [
        "#3B82F6", "#EF4444", "#10B981", "#F59E0B", "#8B5CF6",
        "#EC4899", "#14B8A6", "#F97316", "#6366F1", "#84CC16"
    ];

    // Verificar autenticación
    useEffect(() => {
        if (!user) {
            navigate("/login");
        }
    }, [user, navigate]);

    // Cargar métodos de pago del usuario
    useEffect(() => {
        if (user) {
            loadPaymentMethods();
        }
    }, [user]);

    const loadPaymentMethods = async () => {
        if (!user) return;

        setLoadingMethods(true);
        try {
            const methodsRef = collection(db, "expenses", user.uid, "paymentMethods");
            const q = query(methodsRef, orderBy("createdAt", "desc"));
            const querySnapshot = await getDocs(q);

            const methodsData = [];
            querySnapshot.forEach((doc) => {
                methodsData.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });

            setPaymentMethods(methodsData);
        } catch (error) {
            console.error("Error al cargar métodos de pago:", error);
            setMessage({ text: "Error al cargar los métodos de pago", type: "error" });
        } finally {
            setLoadingMethods(false);
        }
    };

    const handleAddMethod = async () => {
        if (!user || !newMethod.nombre.trim()) {
            setMessage({ text: "El nombre del medio de pago es requerido", type: "error" });
            return;
        }

        // Verificar si ya existe un método con ese nombre
        const existingMethod = paymentMethods.find(
            method => method.nombre.toLowerCase() === newMethod.nombre.trim().toLowerCase()
        );

        if (existingMethod) {
            setMessage({ text: "Ya existe un medio de pago con ese nombre", type: "error" });
            return;
        }

        setLoading(true);
        try {
            const methodsRef = collection(db, "expenses", user.uid, "paymentMethods");
            await addDoc(methodsRef, {
                nombre: newMethod.nombre.trim(),
                tipo: newMethod.tipo,
                entidad: newMethod.entidad.trim(),
                ultimos4: newMethod.ultimos4,
                limite: newMethod.limite ? parseFloat(newMethod.limite) : null,
                fechaVencimiento: newMethod.fechaVencimiento,
                activo: newMethod.activo,
                color: newMethod.color,
                createdAt: new Date(),
                updatedAt: new Date(),
                userId: user.uid,
            });

            setMessage({ text: "Medio de pago creado correctamente", type: "success" });
            setNewMethod({
                nombre: "",
                tipo: "credito",
                entidad: "",
                ultimos4: "",
                limite: "",
                fechaVencimiento: "",
                activo: true,
                color: "#3B82F6"
            });
            setShowAddForm(false);
            loadPaymentMethods();
        } catch (error) {
            console.error("Error al crear medio de pago:", error);
            setMessage({ text: "Error al crear el medio de pago", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleEditMethod = async (methodId, updatedData) => {
        if (!user || !updatedData.nombre.trim()) {
            setMessage({ text: "El nombre del medio de pago es requerido", type: "error" });
            return;
        }

        setLoading(true);
        try {
            const methodRef = doc(db, "expenses", user.uid, "paymentMethods", methodId);
            await updateDoc(methodRef, {
                ...updatedData,
                limite: updatedData.limite ? parseFloat(updatedData.limite) : null,
                updatedAt: new Date(),
            });

            setMessage({ text: "Medio de pago actualizado correctamente", type: "success" });
            setEditingMethod(null);
            loadPaymentMethods();
        } catch (error) {
            console.error("Error al actualizar medio de pago:", error);
            setMessage({ text: "Error al actualizar el medio de pago", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteMethod = async (methodId, methodName) => {
        if (!user || !window.confirm(`¿Está seguro de eliminar el medio de pago "${methodName}"?`)) {
            return;
        }

        setLoading(true);
        try {
            const methodRef = doc(db, "expenses", user.uid, "paymentMethods", methodId);
            await deleteDoc(methodRef);

            setMessage({ text: "Medio de pago eliminado correctamente", type: "success" });
            loadPaymentMethods();
        } catch (error) {
            console.error("Error al eliminar medio de pago:", error);
            setMessage({ text: "Error al eliminar el medio de pago", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const toggleMethodStatus = async (method) => {
        setLoading(true);
        try {
            const methodRef = doc(db, "expenses", user.uid, "paymentMethods", method.id);
            await updateDoc(methodRef, {
                activo: !method.activo,
                updatedAt: new Date(),
            });

            setMessage({
                text: `Medio de pago ${!method.activo ? "activado" : "desactivado"} correctamente`,
                type: "success"
            });
            loadPaymentMethods();
        } catch (error) {
            console.error("Error al cambiar estado:", error);
            setMessage({ text: "Error al cambiar el estado", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const createDefaultMethods = async () => {
        if (!user) return;

        const defaultMethods = [
            {
                nombre: "Efectivo",
                tipo: "efectivo",
                entidad: "",
                ultimos4: "",
                limite: null,
                fechaVencimiento: "",
                activo: true,
                color: "#10B981"
            },
            {
                nombre: "Tarjeta Principal",
                tipo: "credito",
                entidad: "Banco",
                ultimos4: "0000",
                limite: null,
                fechaVencimiento: "",
                activo: true,
                color: "#3B82F6"
            }
        ];

        setLoading(true);
        try {
            const methodsRef = collection(db, "expenses", user.uid, "paymentMethods");

            for (const methodData of defaultMethods) {
                await addDoc(methodsRef, {
                    ...methodData,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    userId: user.uid,
                });
            }

            setMessage({ text: "Medios de pago por defecto creados correctamente", type: "success" });
            loadPaymentMethods();
        } catch (error) {
            console.error("Error al crear medios por defecto:", error);
            setMessage({ text: "Error al crear los medios de pago", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const getTypeIcon = (tipo) => {
        const typeData = methodTypes.find(t => t.value === tipo);
        return typeData ? typeData.icon : CreditCard;
    };

    const getTypeLabel = (tipo) => {
        const typeData = methodTypes.find(t => t.value === tipo);
        return typeData ? typeData.label : tipo;
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <Building2 className="w-16 h-16 text-blue-900 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">Acceso no autorizado</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-6">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate(-1)}
                                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                <ChevronLeft className="w-6 h-6 text-slate-600" />
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-slate-900">Medios de Pago</h1>
                                <p className="text-slate-600 font-medium">Gestione sus tarjetas, cuentas y medios de pago</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-900 to-blue-800 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-lg">X</span>
                            </div>
                            <span className="text-xl font-bold text-blue-900">XFinance</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Add Method Section */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-8">
                    <div className="p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <Plus className="w-5 h-5 text-blue-600" />
                                </div>
                                Nuevo Medio de Pago
                            </h2>
                            {paymentMethods.length === 0 && (
                                <button
                                    onClick={createDefaultMethods}
                                    disabled={loading}
                                    className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 transition-colors disabled:opacity-50"
                                >
                                    Crear Métodos por Defecto
                                </button>
                            )}
                        </div>

                        {showAddForm ? (
                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            Nombre del método *
                                        </label>
                                        <input
                                            type="text"
                                            value={newMethod.nombre}
                                            onChange={(e) => setNewMethod(prev => ({ ...prev, nombre: e.target.value }))}
                                            placeholder="Ej: Visa Principal, Efectivo"
                                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            Tipo de método *
                                        </label>
                                        <select
                                            value={newMethod.tipo}
                                            onChange={(e) => setNewMethod(prev => ({ ...prev, tipo: e.target.value }))}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                        >
                                            {methodTypes.map(type => (
                                                <option key={type.value} value={type.value}>
                                                    {type.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {(newMethod.tipo === "credito" || newMethod.tipo === "debito") && (
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                                Entidad bancaria
                                            </label>
                                            <input
                                                type="text"
                                                value={newMethod.entidad}
                                                onChange={(e) => setNewMethod(prev => ({ ...prev, entidad: e.target.value }))}
                                                placeholder="Ej: BBVA, Santander"
                                                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                                Últimos 4 dígitos
                                            </label>
                                            <input
                                                type="text"
                                                maxLength="4"
                                                value={newMethod.ultimos4}
                                                onChange={(e) => setNewMethod(prev => ({ ...prev, ultimos4: e.target.value.replace(/\D/g, '') }))}
                                                placeholder="1234"
                                                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                                Fecha de vencimiento
                                            </label>
                                            <input
                                                type="month"
                                                value={newMethod.fechaVencimiento}
                                                onChange={(e) => setNewMethod(prev => ({ ...prev, fechaVencimiento: e.target.value }))}
                                                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                            />
                                        </div>
                                    </div>
                                )}

                                {newMethod.tipo === "credito" && (
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                            Límite de crédito
                                        </label>
                                        <div className="relative">
                                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={newMethod.limite}
                                                onChange={(e) => setNewMethod(prev => ({ ...prev, limite: e.target.value }))}
                                                placeholder="0.00"
                                                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                            />
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Color identificativo
                                    </label>
                                    <div className="flex gap-2 flex-wrap">
                                        {colorOptions.map(color => (
                                            <button
                                                key={color}
                                                onClick={() => setNewMethod(prev => ({ ...prev, color }))}
                                                className={`w-8 h-8 rounded-lg border-2 transition-all ${newMethod.color === color ? 'border-slate-400 scale-110' : 'border-slate-200'
                                                    }`}
                                                style={{ backgroundColor: color }}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <button
                                        onClick={handleAddMethod}
                                        disabled={loading || !newMethod.nombre.trim()}
                                        className="px-6 py-3 bg-blue-900 text-white rounded-lg font-semibold hover:bg-blue-800 transition-colors disabled:opacity-50 flex items-center gap-2"
                                    >
                                        <Save className="w-4 h-4" />
                                        Guardar
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowAddForm(false);
                                            setNewMethod({
                                                nombre: "",
                                                tipo: "credito",
                                                entidad: "",
                                                ultimos4: "",
                                                limite: "",
                                                fechaVencimiento: "",
                                                activo: true,
                                                color: "#3B82F6"
                                            });
                                        }}
                                        className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => setShowAddForm(true)}
                                className="w-full p-4 border-2 border-dashed border-slate-300 rounded-lg text-slate-600 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2 font-medium"
                            >
                                <Plus className="w-5 h-5" />
                                Agregar Nuevo Medio de Pago
                            </button>
                        )}
                    </div>
                </div>

                {/* Payment Methods List */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                    <div className="p-6">
                        <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                <Wallet className="w-5 h-5 text-purple-600" />
                            </div>
                            Medios de Pago Registrados
                            <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-full text-sm font-medium">
                                {paymentMethods.length}
                            </span>
                        </h2>

                        {loadingMethods ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                                <span className="ml-3 text-slate-600 font-medium">Cargando medios de pago...</span>
                            </div>
                        ) : paymentMethods.length === 0 ? (
                            <div className="text-center py-12">
                                <CreditCard className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">No hay medios de pago</h3>
                                <p className="text-slate-600 mb-6">Comience agregando su primer medio de pago</p>
                                <div className="flex gap-3 justify-center">
                                    <button
                                        onClick={() => setShowAddForm(true)}
                                        className="bg-blue-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-800 transition-colors flex items-center gap-2"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Nuevo Medio de Pago
                                    </button>
                                    <button
                                        onClick={createDefaultMethods}
                                        disabled={loading}
                                        className="border border-slate-300 text-slate-700 px-6 py-3 rounded-lg font-semibold hover:bg-slate-50 transition-colors disabled:opacity-50"
                                    >
                                        Usar Métodos por Defecto
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="grid gap-4">
                                {paymentMethods.map((method) => {
                                    const IconComponent = getTypeIcon(method.tipo);

                                    return (
                                        <div
                                            key={method.id}
                                            className={`p-6 border rounded-xl transition-all ${method.activo ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50'
                                                }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div
                                                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                                                        style={{
                                                            backgroundColor: `${method.color}15`,
                                                            border: `2px solid ${method.color}25`
                                                        }}
                                                    >
                                                        <IconComponent
                                                            className="w-6 h-6"
                                                            style={{ color: method.color }}
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="flex items-center gap-3">
                                                            <h3 className="font-semibold text-slate-900 text-lg">
                                                                {method.nombre}
                                                            </h3>
                                                            {!method.activo && (
                                                                <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
                                                                    Inactivo
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-slate-600 font-medium">
                                                            {getTypeLabel(method.tipo)}
                                                            {method.entidad && ` • ${method.entidad}`}
                                                            {method.ultimos4 && ` • ****${method.ultimos4}`}
                                                        </p>
                                                        {method.limite && (
                                                            <p className="text-sm text-slate-500">
                                                                Límite: ${parseFloat(method.limite).toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                                                            </p>
                                                        )}
                                                        {method.fechaVencimiento && (
                                                            <p className="text-sm text-slate-500">
                                                                Vence: {new Date(method.fechaVencimiento + '-01').toLocaleDateString("es-MX", { year: 'numeric', month: 'long' })}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => toggleMethodStatus(method)}
                                                        disabled={loading}
                                                        className={`p-2 rounded-lg transition-colors ${method.activo
                                                                ? "bg-green-100 text-green-600 hover:bg-green-200"
                                                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                                            }`}
                                                        title={method.activo ? "Desactivar" : "Activar"}
                                                    >
                                                        {method.activo ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                                                    </button>
                                                    <button
                                                        onClick={() => setEditingMethod(method.id)}
                                                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                                        title="Editar método"
                                                    >
                                                        <Edit3 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteMethod(method.id, method.nombre)}
                                                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                                        title="Eliminar método"
                                                        disabled={loading}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Info Card */}
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-6 mt-8">
                    <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                        <Shield className="w-5 h-5" />
                        Información sobre Medios de Pago
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-blue-800">
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                                <span>Configure todos sus métodos de pago para un seguimiento completo</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                                <span>Los datos de tarjetas se almacenan de forma segura</span>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                                <span>Puede activar/desactivar métodos temporalmente</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                                <span>Use colores para identificar rápidamente cada método</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Toast Message */}
            {message.text && (
                <div
                    className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg transition-all duration-300 z-50 max-w-md ${message.type === "error"
                            ? "bg-red-50 border border-red-200 text-red-800"
                            : "bg-green-50 border border-green-200 text-green-800"
                        }`}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {message.type === "error" ? (
                                <AlertCircle className="w-5 h-5 text-red-600" />
                            ) : (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            )}
                            <span className="font-semibold">{message.text}</span>
                        </div>
                        <button
                            onClick={() => setMessage({ text: "", type: "" })}
                            className="ml-4 p-1 hover:bg-white/50 rounded-full transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default XFinancePaymentMethods;